import React from 'react';
import { isDemoMode, DEMO_UNAVAILABLE_TITLE } from './demoMode';

// The manual resolver PERSISTS the resolved name onto the alert row, so it's a
// write — unavailable in Demo Mode (shown as a muted note, not an error toast).
const DEMO = isDemoMode();

// ─────────────────────────────────────────────────────────────────────────────
// Directory object (GUID → friendly name) helpers, shared by AlertsPage and
// TestRunResultsPage (rule #13 display parity).
//
// directory_audit alerts — most visibly Conditional Access policy modifications —
// reference groups / users / directory roles by bare GUID inside
// modifiedProperties (e.g. conditions.users.includeGroups: Added <guid>). These
// helpers collect those GUIDs so the page can resolve them via the
// ResolveDirectoryObjects endpoint, and render the resolved name alongside the
// raw GUID.
// ─────────────────────────────────────────────────────────────────────────────

const GUID_RE_ANCHORED = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GUID_RE_GLOBAL   = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function isGuid(v) {
  return typeof v === 'string' && GUID_RE_ANCHORED.test(v.trim());
}

// ── Minimal, self-contained replica of the page-local diff so the collector
//    picks up EXACTLY the GUIDs that get rendered (and no more). Collecting only
//    rendered identity references — not the policy id / tenant id / other object
//    ids in the blob — keeps the resolver's audit-stream fallback from firing on
//    every expand (it only fires when a rendered GUID is genuinely unresolved,
//    e.g. an object created the same day). Mirrors computeJsonDiff in AlertsPage
//    / TestRunResultsPage. ─────────────────────────────────────────────────────
const DIFF_SKIP_ROOTS = new Set(['id', 'createdDateTime', 'modifiedDateTime', 'displayName']);

function tryParseAsObject(v) {
  if (!v) return null;
  let val = v;
  for (let i = 0; i < 3 && typeof val === 'string'; i++) {
    try { val = JSON.parse(val); } catch { return null; }
  }
  return (val && typeof val === 'object' && !Array.isArray(val)) ? val : null;
}

function flattenObj(obj, prefix = '') {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObj(value, path));
    } else {
      result[path] = value;
    }
  }
  return result;
}

function serVal(v) {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function isNullishVal(v) {
  if (v === null || v === undefined || v === '') return true;
  if (typeof v === 'string') { const t = v.trim(); return t === '' || t === 'null'; }
  return false;
}

function addGuid(v, set) {
  if (isGuid(v)) set.add(v.trim().toLowerCase());
}

// Collect the GUIDs that the Target Resource panel actually renders as
// identity values — array added/removed items, changed/added/removed scalars,
// and scalar leaves of a created/deleted object. Returns a deduped lowercased
// array. This is the set the page asks ResolveDirectoryObjects to name.
export function collectTargetResourceGuids(rawEvent) {
  const out = new Set();
  const targets = rawEvent?.targetResources;
  if (!Array.isArray(targets)) return [];
  for (const t of targets) {
    const props = t?.modifiedProperties;
    if (!Array.isArray(props)) continue;
    for (const mp of props) {
      const oldParsed = tryParseAsObject(mp?.oldValue);
      const newParsed = tryParseAsObject(mp?.newValue);

      if (oldParsed && newParsed) {
        const oldFlat = flattenObj(oldParsed);
        const newFlat = flattenObj(newParsed);
        const allPaths = new Set([...Object.keys(oldFlat), ...Object.keys(newFlat)]);
        for (const path of allPaths) {
          if (DIFF_SKIP_ROOTS.has(path.split('.')[0])) continue;
          const inOld = path in oldFlat, inNew = path in newFlat;
          if (inOld && inNew) {
            if (serVal(oldFlat[path]) === serVal(newFlat[path])) continue;
            if (Array.isArray(oldFlat[path]) && Array.isArray(newFlat[path])) {
              const os = new Set(oldFlat[path].map(serVal));
              const ns = new Set(newFlat[path].map(serVal));
              newFlat[path].filter(x => !os.has(serVal(x))).forEach(x => addGuid(x, out)); // added items
              oldFlat[path].filter(x => !ns.has(serVal(x))).forEach(x => addGuid(x, out)); // removed items
            } else {
              addGuid(oldFlat[path], out);
              addGuid(newFlat[path], out);
            }
          } else if (inNew) {
            addGuid(newFlat[path], out);
          } else {
            addGuid(oldFlat[path], out);
          }
        }
        continue;
      }

      // One-sided object create/delete — collect scalar leaf GUIDs (arrays in
      // this path render unannotated, so they're intentionally skipped).
      const oneSide = (newParsed && isNullishVal(mp?.oldValue)) ? newParsed
                    : ((oldParsed && isNullishVal(mp?.newValue)) ? oldParsed : null);
      if (oneSide) {
        const flat = flattenObj(oneSide);
        for (const [path, value] of Object.entries(flat)) {
          if (DIFF_SKIP_ROOTS.has(path.split('.')[0])) continue;
          addGuid(value, out);
        }
      }
    }
  }
  return Array.from(out);
}

// Format a diff/leaf value for display — mirrors the page-local diffVal() so a
// non-resolved value renders identically to before.
function formatScalar(v) {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  if (typeof v === 'string') return v.length > 80 ? v.slice(0, 77) + '…' : v;
  if (Array.isArray(v)) {
    if (v.length === 0) return '[]';
    const items = v.map(x => (typeof x === 'string' ? x : JSON.stringify(x)));
    const joined = items.join(', ');
    return joined.length > 120 ? `[${v.length} items]` : `[${joined}]`;
  }
  return JSON.stringify(v);
}

const TYPE_LABEL = { group: 'Group', user: 'User', role: 'Role' };

// Render a modifiedProperties value. When the value is a GUID resolved by the
// directory-name lookup, show the friendly name + a small type badge with the
// raw GUID muted after it. Otherwise render exactly like diffVal() did — and,
// when a GUID is still unresolved and an onResolve handler is supplied, offer a
// "Resolve Name" button (manual ATS lookup) with per-id resolving / not-found
// states.
//   value        — the raw value being rendered
//   names        — resolved map { "<idLower>": { name, type, upn? } }
//   valueClass   — the tone class the caller used on the value span
//   onResolve    — optional (id) => void; enables the "Resolve Name" button on
//                  unresolved GUIDs (AlertsPage real alerts only)
//   resolvingIds — optional Set of ids currently being resolved
//   notFoundIds  — optional Set of ids the last attempt could not resolve
export function ResolvedId({ value, names, valueClass = '', onResolve, resolvingIds, notFoundIds }) {
  const id = isGuid(value) ? value.trim().toLowerCase() : null;
  const hit = id && names ? names[id] : null;

  if (hit && hit.name) {
    return (
      <span className="inline-flex items-baseline gap-1.5 flex-wrap">
        <span className={`${valueClass} font-medium`}>{hit.name}</span>
        {hit.type && (
          <span className="px-1 py-0.5 rounded text-[10px] font-medium bg-gray-800 text-gray-400 border border-gray-700/60">
            {TYPE_LABEL[hit.type] || hit.type}
          </span>
        )}
        <span className="text-gray-500 text-[10px] font-mono">{value}</span>
      </span>
    );
  }

  // Unresolved. Render the raw value, plus the optional resolve affordance for GUIDs.
  const showResolve = id && typeof onResolve === 'function';
  const isResolving = showResolve && resolvingIds && resolvingIds.has(id);
  const isNotFound  = showResolve && notFoundIds && notFoundIds.has(id);
  return (
    <span className="inline-flex items-baseline gap-2 flex-wrap">
      <span className={valueClass}>{formatScalar(value)}</span>
      {showResolve && (
        DEMO ? (
          <span className="text-[10px] text-gray-500 italic" title={DEMO_UNAVAILABLE_TITLE}>
            name lookup unavailable in Demo Mode
          </span>
        ) : isResolving ? (
          <span className="text-[10px] text-gray-400 italic">Resolving…</span>
        ) : isNotFound ? (
          <span className="text-[10px] text-amber-400/90 italic">not found — try again after 30 min</span>
        ) : (
          <button
            type="button"
            onClick={() => onResolve(id)}
            className="text-[10px] px-1.5 py-0.5 rounded border border-sky-700/60 bg-sky-900/40 text-sky-300 hover:bg-sky-800/50 transition-colors"
            title="Look up this object's name in directory data and save it to the alert"
          >
            Resolve Name
          </button>
        )
      )}
    </span>
  );
}
